/**
 * Installs `globalThis.crypto.getRandomValues` when the runtime lacks it.
 *
 * ⚠️  Import this before anything that seals a key transfer. Hermes ships no
 *     global `crypto` — that absence is why this app splits `crypto.native.ts`
 *     from `crypto.web.ts` — and React Native 0.83 does not add one. The HPKE
 *     stack behind `key-transfer.ts` reaches for `crypto.getRandomValues`
 *     directly when it generates ephemeral keys, so without this the transfer
 *     screens throw on device while working fine on web and in tests.
 *
 * The randomness comes from `expo-crypto`, the same source `crypto.native.ts`
 * already uses, so every random byte in the app still originates in one place.
 * On web and under Node the global already exists and this is a no-op.
 */

import * as ExpoCrypto from 'expo-crypto';

type RandomFill = <T extends ArrayBufferView | null>(array: T) => T;

function install(): void {
  const existing = (globalThis as { crypto?: Partial<Crypto> }).crypto;

  if (typeof existing?.getRandomValues === 'function') return;

  const getRandomValues = ((array) =>
    ExpoCrypto.getRandomValues(array as never)) as RandomFill;

  if (existing) {
    // `crypto` exists but is missing the method — patch in place.
    (existing as { getRandomValues: RandomFill }).getRandomValues = getRandomValues;
    return;
  }

  // No `crypto` at all. defineProperty rather than assignment: on some runtimes
  // the global is a non-writable accessor and a plain assignment fails silently.
  Object.defineProperty(globalThis, 'crypto', {
    configurable: true,
    enumerable: false,
    writable: true,
    value: { getRandomValues },
  });
}

install();
